<template>
  <ConfirmDialog></ConfirmDialog>
  <Toast />

  <div class="flex align-items-center gap-2 text-sm text-500 mb-2 px-1">
    <span class="font-semibold">Ventas</span>
    <i class="pi pi-angle-right text-xs"></i> <span>Listado de Ventas</span>
  </div>

  <div class="surface-card shadow-1 border-round p-2">
    <div class="flex flex-wrap align-items-center justify-content-between mb-2 gap-2">
      <div class="flex align-items-center gap-2">
        <span class="text-lg font-bold text-900">Administrar Ventas</span>
        <Badge size="xlarge" severity="success">{{ totalRecords }}</Badge>
      </div>
      <div class="flex gap-2">
        <Button
          v-if="'Venta Crear' in auth.user.permissions"
          label="Nueva Venta"
          icon="pi pi-plus"
          class="p-button-info p-button-lg btn-nueva-venta"
          @click="nuevaVenta"
        />
      </div>
    </div>

    <div class="surface-100 p-2 border-round mb-2 border-1 surface-border flex gap-2 align-items-end flex-wrap">
      <div class="flex-2" style="min-width: 150px">
        <strong>CÓDIGO:</strong>
        <InputNumber v-model="filtros.codigo" :useGrouping="false" class="w-full" @keyup.enter="buscarVentas" />
      </div>
      <div class="flex-1" style="min-width: 250px">
        <strong>CLIENTE / NIT:</strong>
        <div class="p-input-icon-right w-full">
          <InputText v-model="filtros.buscar" class="w-full" @keyup.enter="buscarVentas" />
          <i class="pi pi-search" @click="buscarVentas" />
        </div>
      </div>
      <div class="flex-1" style="min-width: 200px">
        <strong>VENDEDOR:</strong>
        <div class="p-input-icon-right w-full">
          <InputText v-model="filtros.vendedor" class="w-full" @keyup.enter="buscarVentas" />
          <i class="pi pi-search" @click="buscarVentas" />
        </div>
      </div>
      <div>
        <Button
          icon="pi pi-search"
          label="BUSCAR"
          class="p-button-primary p-button-lg p-button-outlined"
          @click="buscarVentas"
        />
      </div>
    </div>

    <DataTable
      ref="ventas"
      :value="ventas"
      dataKey="id"
      :lazy="true"
      :paginator="true"
      :rows="lazyParams.rows"
      :totalRecords="totalRecords"
      :loading="cargando"
      @page="onPage"
      class="p-datatable-sm"
      selectionMode="single click"
      :rowsPerPageOptions="[20, 50, 100]"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
      currentPageReportTemplate="Mostrando {first} de {last} de {totalRecords} Ventas"
      responsiveLayout="scroll"
      showGridlines
      stripedRows
    >
      <template #empty>
        <div class="text-center p-4 text-sm">No existen Registros!</div>
      </template>
      <Column field="id" header="CÓDIGO" class="text-center"></Column>
      <Column field="nombre_usuario_caja" header="VENDEDOR"></Column>
      <Column field="nombre_cliente" header="CLIENTE">
        <template #body="{ data }">
          <span style="font-weight: bold">{{ data.nombre_cliente }}</span>
        </template>
      </Column>

      <Column field="nit" header="NIT/CI">
        <template #body="{ data }">
          <div class="nit-container">
            <span>{{ data.nit || 'S/N' }}</span>
            <i
              v-if="data.razon_social && data.razon_social !== data.nombre_cliente"
              class="pi pi-info-circle info-icon"
              v-tooltip.top="data.razon_social"
            ></i>
          </div>
        </template>
      </Column>

      <Column header="F.VENTA" class="text-center">
        <template #body="{ data }">
          <div class="fecha-container">
            {{ formatDate(data.fecha_venta) }}
            {{ formatTime(data.fecha_venta) }}
          </div>
        </template>
      </Column>

      <!-- Columna DESC. solo se muestra si hay descuentos -->
      <Column v-if="hasDescuentos" field="descuento" header="DESC." class="text-right">
        <template #body="{ data }">
          <span v-if="data.descuento > 0" class="descuento-badge">
            -{{ convertirNumeroGermanicFormat(data.descuento) }}
          </span>
          <span v-else class="text-400">—</span>
        </template>
      </Column>

      <Column
        field="total_cancelar"
        header="T.VENTA"
        class="text-right font-bold"
        v-if="'Caja Ver Detalle' in auth.user.permissions"
      >
        <template #body="{ data }">
          <span class="monto-total">{{ convertirNumeroGermanicFormat(data.total_cancelar) }}</span>
        </template>
      </Column>

      <Column
        field="calculo_efectivo"
        header="EFECTIVO"
        class="text-right"
        v-if="'Caja Ver Detalle' in auth.user.permissions"
      >
        <template #body="{ data }">
          <span class="monto-efectivo">{{ convertirNumeroGermanicFormat(data.calculo_efectivo) }}</span>
        </template>
      </Column>

      <Column
        field="transferencias"
        header="TRANSF."
        class="text-right"
        v-if="'Caja Ver Detalle' in auth.user.permissions"
      >
        <template #body="{ data }">
          <span class="monto-transferencia">{{ convertirNumeroGermanicFormat(data.transferencias) }}</span>
        </template>
      </Column>

      <Column header="ENVIADO" class="text-center" v-if="'Venta Facturar' in auth.user.permissions">
        <template #body="{ data }">
          <Tag
            :value="data.facturado === 1 ? 'SI' : 'NO'"
            :severity="data.facturado === 1 ? 'success' : 'danger'"
            class="tag-large"
          />
        </template>
      </Column>

      <Column header="ESTADO" class="text-center">
        <template #body="{ data }">
          <Tag :value="data.estado_texto" :severity="data.estado ? 'success' : 'danger'" class="tag-large" />
        </template>
      </Column>

      <Column header="ACCIONES" class="text-center">
        <template #body="slotProps">
          <Button
            icon="pi pi-ellipsis-v"
            class="p-button-icon-only p-button-raised p-button-info"
            v-tooltip.top="'Ver Acciones'"
            @click="$refs['menu' + slotProps.data.id].toggle($event)"
          />
          <Menu :ref="'menu' + slotProps.data.id" :popup="true" :model="acciones(slotProps.data)" />
        </template>
      </Column>
    </DataTable>

    <Dialog v-model:visible="mostrar_qr" modal header="FACTURA QR" :style="{ width: '250px' }">
      <div class="flex justify-content-center">
        <img :src="'data:image/png;base64, ' + qr" alt="QR" :style="{ width: '200px', height: '200px' }" />
      </div>
    </Dialog>

    <Dialog v-model:visible="motivoAnulacionDialog" modal header="MOTIVO DE ANULACIÓN" :style="{ width: '450px' }">
      <div class="p-fluid">
        <div class="field">
          <label for="motivo_anulacions" class="font-semibold mb-2"> SELECCIONE MOTIVO DE ANULACIÓN: </label>
          <Dropdown
            id="motivo_anulacions"
            v-model="motivoAnulacionSelected"
            :options="motivo_anulacions"
            optionLabel="descripcion"
            optionValue="codigoClasificador"
            placeholder="Seleccione un motivo"
            class="w-full"
          />
        </div>
      </div>
      <template #footer>
        <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="motivoAnulacionDialog = false" />
        <Button
          label="Anular Factura"
          icon="pi pi-trash"
          class="p-button-danger"
          :loading="enviando"
          @click="anularVentaFactura(anularVentaId)"
        />
      </template>
    </Dialog>
  </div>

  <VentaFacturar
    v-if="ventafacturaDialog"
    :show="ventafacturaDialog"
    :ventaFactura="ventaFactura"
    @closeModal="ocultarDialog"
    @actualizarListado="cargarVentas"
  />
  <VentaMetodoPago
    v-if="metodoPagoDialog"
    :show="metodoPagoDialog"
    :metodoPago="metodoPago"
    @closeModal="cerrarModalMetodoPago"
    @actualizarListado="cargarVentas"
  />
</template>

<script>
import VentaService from '@/service/VentaService'
import { useAuth } from '@/stores'
import VentaFacturar from '@/module/ventas/VentaFacturar.vue'
import VentaMetodoPago from '@/module/ventas/VentaMetodoPago.vue'

export default {
  components: { VentaFacturar, VentaMetodoPago },
  data() {
    return {
      ventas: [],
      totalRecords: 0,
      cargando: false,
      lazyParams: { page: 1, rows: 20 },
      filtros: {
        codigo: null,
        buscar: '',
        vendedor: ''
      },
      qr: '',
      mostrar_qr: false,
      ventaFactura: {},
      ventafacturaDialog: false,
      metodoPagoDialog: false,
      metodoPago: {},
      motivo_anulacions: [],
      motivoAnulacionSelected: null,
      motivoAnulacionDialog: false,
      anularVentaId: null,
      enviando: false
    }
  },
  auth: null,
  created() {
    this.auth = useAuth()
    this.ventaService = new VentaService()
  },
  mounted() {
    this.cargarVentas()
    //this.cargarMotivoAnulacion();
  },
  computed: {
    hasDescuentos() {
      return this.ventas.some((v) => parseFloat(v.descuento) > 0)
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },

    formatTime(dateString) {
      const date = new Date(dateString)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },

    convertirNumeroGermanicFormat(numero) {
      if (!numero && numero !== 0) return '0.00'
      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(parseFloat(numero))
    },

    cargarMotivoAnulacion() {
      if (this.motivo_anulacions.length > 0) return
      this.ventaService.motivo_anulacions().then((data) => {
        this.motivo_anulacions = data.motivo_anulacions || []
        this.motivoAnulacionSelected = this.motivo_anulacions[0]?.codigoClasificador || null
      })
    },

    motivoAnulacionDialogConfirm(datos) {
      this.anularVentaId = datos.id
      this.motivoAnulacionDialog = true
      this.enviando = false
      this.cargarMotivoAnulacion()
    },

    cargarVentas() {
      this.cargando = true

      const params = {
        page: this.lazyParams.page,
        per_page: this.lazyParams.rows,
        ...this.filtros
      }

      Object.keys(params).forEach((key) => {
        if (params[key] === null || params[key] === '' || params[key] === undefined) {
          delete params[key]
        }
      })

      if (params.codigo) {
        params.codigo = parseInt(params.codigo)
      }

      this.ventaService
        .getVentasPaginadas(params)
        .then((response) => {
          if (response && response.data) {
            this.ventas = response.data
            this.totalRecords = response.total || response.data.length

            this.ventas = this.ventas.map((venta) => ({
              ...venta,
              estado_texto: venta.estado_texto || (venta.estado ? 'VÁLIDO' : 'ANULADO')
            }))
          } else {
            this.ventas = []
            this.totalRecords = 0
          }
          this.cargando = false
        })
        .catch((error) => {
          console.error('Error cargando ventas:', error)
          this.ventas = []
          this.totalRecords = 0
          this.cargando = false
        })
    },

    formatDateForAPI(dateString) {
      if (!dateString) return ''
      if (dateString.includes('/')) {
        const parts = dateString.split('/')
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`
        }
      }
      return dateString
    },

    onPage(event) {
      this.lazyParams.page = event.page + 1
      this.lazyParams.rows = event.rows
      this.cargarVentas()
    },

    buscarVentas() {
      this.lazyParams.page = 1
      this.cargarVentas()
    },

    acciones(datos) {
      return [
        {
          label: 'Ver Detalle',
          icon: 'pi pi-fw pi-eye',
          disabled: 'Venta Ver Detalle' in this.auth.user.permissions ? false : true,
          to: `/ventas/${datos.id}/detalle`
        },
        {
          label: 'Descargar PDF',
          disabled: 'Venta DescargarPDF' in this.auth.user.permissions ? false : true,
          icon: 'pi pi-fw pi-file-pdf',
          command: () => this.imprimirVenta(datos.id)
        },
        {
          label: 'Mostrar Factura',
          disabled: datos.facturado ? false : true,
          icon: 'pi pi-fw pi-file',
          command: () => this.mostrar_factura(datos)
        },
        {
          label: 'Crear Factura',
          disabled: datos.estado == 0 ? true : false || 'Venta Facturar' in this.auth.user.permissions ? false : true,
          icon: 'pi pi-fw pi-file',
          visible: datos.facturado == 0 ? true : false,
          command: () => this.ventaFacturar(datos)
        },
        {
          label: 'Anular Venta',
          icon: 'pi pi-fw pi-trash',
          disabled: datos.estado == 0 ? true : false || 'Venta Anular' in this.auth.user.permissions ? false : true,
          visible: datos.facturado == 1 ? false : true,
          command: () => this.anularVenta(datos.id)
        },
        {
          label: 'Anular Factura',
          icon: 'pi pi-fw pi-trash',
          tooltip: 'Anular Factura',
          disabled: datos.estado == 0 ? true : false || 'Venta Anular' in this.auth.user.permissions ? false : true,
          visible: datos.facturado == 1 ? true : false,
          command: () => this.motivoAnulacionDialogConfirm(datos)
        },
        {
          label: 'Cambiar Método de Pago',
          icon: 'pi pi-fw pi-money-bill',
          disabled: datos.estado == 0 ? true : false,
          command: () => this.ventaMetodoPago(datos)
        }
      ]
    },

    ventaMetodoPago(datos) {
      this.metodoPago = { ...datos }
      this.metodoPagoDialog = true
    },

    cerrarModalMetodoPago() {
      this.metodoPagoDialog = false
    },

    mostrar_factura(datos) {
      this.qr = datos.factura?.qr || ''
      this.mostrar_qr = true
    },

    ventaFacturar(venta) {
      this.ventaFactura = {
        ...venta,
        nit: venta.nit && venta.nit !== '0' && venta.nit !== '99001' ? Number(venta.nit) : null
      }
      this.ventafacturaDialog = true
    },
    ocultarDialog() {
      this.ventafacturaDialog = false
    },

    anularVentaFactura(id) {
      this.enviando = true
      let datos = {
        id: id,
        motivoAnulacion: this.motivoAnulacionSelected
      }
      this.ventaService
        .anularVenta(id, datos)
        .then((response) => {
          this.$toast.add({
            severity: response.status == 200 ? 'success' : 'error',
            summary: response.status == 200 ? 'Éxito' : 'Error',
            detail: response.mensaje || response.message,
            life: 3000
          })
          if (response.status == 200) {
            this.cargarVentas()
            this.motivoAnulacionDialog = false
          }
          this.enviando = false
        })
        .catch(() => {
          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al anular la venta',
            life: 3000
          })
          this.enviando = false
        })
    },

    anularVenta(id) {
      this.$confirm.require({
        header: 'CONFIRMACIÓN',
        message: '¿ESTÁS SEGURO DE ANULAR ESTA VENTA?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Sí, Anular',
        rejectLabel: 'No, Cancelar',
        acceptClass: 'p-button-danger',
        rejectClass: 'p-button-text',
        accept: () => {
          this.ventaService
            .anularVenta(id)
            .then((response) => {
              this.$toast.add({
                severity: response.status == 200 ? 'success' : 'error',
                summary: response.status == 200 ? 'Éxito' : 'Error',
                detail: response.mensaje || response.message,
                life: 3000
              })
              if (response.status == 200) {
                this.cargarVentas()
              }
            })
            .catch(() => {
              this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al anular la venta',
                life: 3000
              })
            })
        }
      })
    },

    nuevaVenta() {
      this.$router.push({ name: 'nueva_venta' })
    },

    imprimirVenta(id) {
      this.ventaService
        .imprimirVenta(id)
        .then(() => {})
        .catch((error) => console.log(error))
    }
  }
}
</script>

<style scoped lang="scss">
.nit-container {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
  font-weight: 500;
}

.info-icon {
  color: #0284c7;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;

  &:hover {
    color: #0369a1;
  }
}

/* Fecha Container - Todo en una línea */
.fecha-container {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
}

/* Montos */
.monto-total {
  color: #0f172a;
  font-weight: 600;
  font-size: 1rem;
}

.monto-efectivo {
  color: #000000;
  font-weight: 500;
  font-size: 1rem;
}

.monto-transferencia {
  color: #000000;
  font-weight: 500;
  font-size: 1rem;
}

.descuento-badge {
  background: #fee2e2;
  color: #dc2626;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.85rem;
  display: inline-block;
}
.tag-large {
  font-size: 0.9rem;
  font-weight: 600;
}
</style>
